import React, { FunctionComponent, useCallback, useMemo, useState } from 'react'
import { useDebounce } from 'react-use'
import { Contact, ContactConfiguration, RecipientContact } from '../../../../../model/Contact'
import { ContactCard, ContactSearchAutocomplete, PatientCard } from '../../../../contact'
import { DocumentContactsFormProps } from './DocumentContactsForm.model'
import { mapContactToRecipientContact } from '../../../../../misc/contact.utilities'
import { useCurrentPatient } from '../../../../../hooks/utils'
import { ContactConfigureBottomPanel } from '../../../../shared/bottomPanel/ContactConfigureBottomPanel'
import { ContactBottomPanel } from '../../../../shared/bottomPanel/ContactBottomPanel'
import { BottomPanelContactsContext } from '../../../../shared/bottomPanel/ContactBottomPanel/ContactBottomPanel.model'
import styles from './DocumentContactsForm.module.scss'
import { Filters } from '../../../../../model/Filters'
import { useGetInfiniteContacts } from '../../../../../hooks/queries/contact'

export const DocumentContactsForm: FunctionComponent<DocumentContactsFormProps> = ({
  addError,
  contacts,
  onChangeContacts,
  onChangePatientRecipient,
  isEditable = true,
  patientAsCopyRecipient,
  patientAsMainRecipient,
}) => {
  const { currentPatient: patient } = useCurrentPatient()
  const [searchValue, setSearchValue] = useState('')
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null)
  const [showContactBottomPanel, setShowContactBottomPanel] = useState(false)
  const [showGreetingsBottomPanel, setShowGreetingsBottomPanel] = useState(false)
  const [filters, setFilters] = useState<Filters>()

  const {
    query: { isLoading },
    contactsList,
    cancelPendingQuery,
  } = useGetInfiniteContacts({ enabled: filters?.search?.length > 1, filters })

  useDebounce(
    () => {
      handleSearchContact()
    },
    400,
    [searchValue],
  )

  const handleSearchContact = () => {
    if (searchValue.length > 1) {
      setFilters({
        search: searchValue,
        withOrganizationOnly: 'true',
      })
    } else {
      isLoading && cancelPendingQuery()
    }
  }

  function onSelectContact(contact: Contact) {
    // Gestion des doublons
    if (!contacts.find(({ id }) => contact.id === id)) {
      const updatedContacts: RecipientContact[] = [...contacts]

      try {
        const newContact =  {
            ...contact,
            ...mapContactToRecipientContact(contact),
          };

        updatedContacts.push(newContact);
      } catch (error) {
        const { message } = error as Error
        if (message) addError(message)
        console.error('Error selecting contact:', error)
      } finally {
        if (updatedContacts?.length) onChangeContacts(updatedContacts)
        setSearchValue('')
      }
    }
  }

  function handleChangeCopyRecipient(contactId: string, value: boolean) {
    onChangeContacts(
      contacts.map((contact) => ({
        ...contact,
        copyRecipient: contact.id === contactId ? value : contact.copyRecipient,
        mainRecipient: contact.id === contactId ? false : contact.mainRecipient,
      })),
    )
  }

  function handleChangeMainRecipient(contactId: string, value: boolean) {
    onChangeContacts(
      contacts.map((contact) => ({
        ...contact,
        copyRecipient: contact.id === contactId ? false : contact.copyRecipient,
        mainRecipient: contact.id === contactId ? value : contact.mainRecipient,
      })),
    )
  }

  function handleChangeOrganization(contactId: string, organizationId: string) {
    onChangeContacts(
      contacts.map((contact) => {
        const organization =
          contact.id === contactId
            ? contact?.organizations.find(({ id }) => id === organizationId) || contact.organization
            : contact?.organization
        return {
          ...contact,
          organization,
        }
      }),
    )
  }

  function handleChangeMssEmail(contactId: string, mssEmailId: string) {
    onChangeContacts(
      contacts.map((contact) => {
        const mssEmail =
          contact.id === contactId
            ? contact?.mssEmails.find(({ id }) => id === mssEmailId) || contact.mssEmail
            : contact?.mssEmail
        return {
          ...contact,
          mssEmail,
        }
      }),
    )
  }

  function handleChangePatientCopyRecipient(copyRecipient: boolean) {
    onChangePatientRecipient({
      patientAsCopyRecipient: copyRecipient,
      patientAsMainRecipient: copyRecipient ? false : patientAsMainRecipient,
    })
  }

  function handleChangePatientMainRecipient(mainRecipient: boolean) {
    onChangePatientRecipient({
      patientAsCopyRecipient: mainRecipient ? false : patientAsCopyRecipient,
      patientAsMainRecipient: mainRecipient,
    })
  }

  const handleCreateContact = useCallback(() => {
    setShowContactBottomPanel(true)
  }, [])

  const handleEditContact = useCallback((id: string) => {
    setShowContactBottomPanel(true)
    setSelectedContactId(id)
  }, [])

  const handleCloseContactBottomPanel = useCallback(() => {
    setShowContactBottomPanel(false)
    setSelectedContactId(null)
  }, [])

  const handleEditContactGreetings = useCallback((id: string) => {
    setShowGreetingsBottomPanel(true)
    setSelectedContactId(id)
  }, [])

  const handleCloseGreetingsBottomPanel = useCallback(() => {
    setShowGreetingsBottomPanel(false)
    setSelectedContactId(null)
  }, [])

  const handleContactGreetingEdited = useCallback(
    (item: ContactConfiguration) => {
      onChangeContacts(
        contacts.map((contact) => ({
          ...contact,
          contactConfiguration:
            selectedContactId === contact.id ? item : contact.contactConfiguration,
        })),
      )
      handleCloseGreetingsBottomPanel()
    },
    [onChangeContacts, contacts, handleCloseGreetingsBottomPanel, selectedContactId],
  )

  const showContacts = useMemo(
    () => searchValue.length > 1 && searchValue === filters?.search,
    [filters, searchValue],
  )

  return (
    <>
      <div className={styles.content}>
        <ContactSearchAutocomplete
          searchValue={searchValue}
          predictions={showContacts ? contactsList : []}
          loading={isLoading}
          onSearchValueChange={(event) => setSearchValue(event.target.value)}
          onSelectContact={onSelectContact}
          createContact={handleCreateContact}
          onFocus={handleSearchContact}
          colorPreset="light"
          disabled={!isEditable}
        />
        <div className={styles.contacts}>
          {contacts.map((contact, index) => (
            <div key={index} className={styles.contact}>
              <ContactCard
                showAddresses
                contact={contact}
                loading={false}
                disabled={!isEditable}
                recipientDisabled={!isEditable}
                onClickDisplayDetails={handleEditContact}
                onClickDisplayConfiguration={handleEditContactGreetings}
                onChangeCopyRecipient={(event) =>
                  handleChangeCopyRecipient(contact.id, event.target.checked)
                }
                onChangeMainRecipient={(event) =>
                  handleChangeMainRecipient(contact.id, event.target.checked)
                }
                onChangeOrganization={(organizationId) =>
                  handleChangeOrganization(contact.id, organizationId)
                }
                onChangeMssEmail={(mssEmailId) => handleChangeMssEmail(contact.id, mssEmailId)}
              />
            </div>
          ))}
          {patient && (
            <div className={styles.contact}>
              <PatientCard
                patient={patient}
                disabled={!isEditable}
                copyRecipient={patientAsCopyRecipient}
                mainRecipient={patientAsMainRecipient}
                onChangeCopyRecipient={(e) => handleChangePatientCopyRecipient(e.target.checked)}
                onChangeMainRecipient={(e) => handleChangePatientMainRecipient(e.target.checked)}
              />
            </div>
          )}
        </div>
      </div>
      <ContactConfigureBottomPanel
        patientId={patient?.id}
        itemId={selectedContactId}
        onItemEdited={handleContactGreetingEdited}
        onRequestClose={handleCloseGreetingsBottomPanel}
        display={showGreetingsBottomPanel}
      />
      <ContactBottomPanel
        addError={addError}
        patientId={patient?.id}
        contactId={selectedContactId}
        display={showContactBottomPanel}
        onRequestClose={handleCloseContactBottomPanel}
        context={BottomPanelContactsContext.DOCUMENT_CONTACT}
      />
    </>
  )
}
